import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Car, User, Menu, X, LogOut, LayoutDashboard, Crown, Star, Shield, Globe } from "lucide-react";
import { useState } from "react";

export const Layout = () => {
  const { user, logout, isAuthenticated, isPremium } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isAdmin = user?.role === "admin";

  const navLinks = [
    { path: "/", label: t("nav_home") },
    { path: "/services", label: t("nav_services") },
    { path: "/products", label: t("nav_products") },
    { path: "/booking", label: t("nav_booking") },
    { path: "/membership", label: t("nav_membership") },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/icons/icon-72x72.png" alt="Adrian's Logo" className="h-10 w-10 rounded-sm" />
              <span className="text-lg font-bold tracking-tight text-white font-['Chivo'] hidden sm:inline">
                Adrian's
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? "text-cyan-400"
                      : "text-zinc-400 hover:text-white"
                  }`}
                  data-testid={`nav-${link.path.replace("/", "") || "home"}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right Section */}
            <div className="hidden md:flex items-center gap-3">
              {/* Language Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-zinc-400 hover:text-white"
                data-testid="language-toggle"
              >
                <Globe className="h-4 w-4 mr-1" />
                {language.toUpperCase()}
              </Button>

              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 border-zinc-700 bg-zinc-800/50 hover:bg-zinc-800"
                      data-testid="user-menu-trigger"
                    >
                      <User className="h-4 w-4" />
                      <span className="max-w-[100px] truncate">{user?.name}</span>
                      {isPremium && <Crown className="h-4 w-4 text-cyan-400" />}
                      {isAdmin && <Shield className="h-4 w-4 text-amber-400" />}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-zinc-800">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="dashboard-link">
                        <LayoutDashboard className="h-4 w-4" />
                        {t("nav_dashboard")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/loyalty" className="flex items-center gap-2 cursor-pointer" data-testid="loyalty-link">
                        <Star className="h-4 w-4" />
                        {t("loyalty_title")}
                        <span className="ml-auto text-xs text-cyan-400">{user?.loyalty_points || 0}</span>
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 cursor-pointer text-amber-400" data-testid="admin-link">
                          <Shield className="h-4 w-4" />
                          {t("nav_admin")}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {!isPremium && (
                      <DropdownMenuItem asChild>
                        <Link to="/membership" className="flex items-center gap-2 cursor-pointer text-cyan-400" data-testid="upgrade-link">
                          <Crown className="h-4 w-4" />
                          Premium werden
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem
                      onClick={logout}
                      className="flex items-center gap-2 cursor-pointer text-red-400"
                      data-testid="logout-button"
                    >
                      <LogOut className="h-4 w-4" />
                      {t("nav_logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-zinc-400 hover:text-white" data-testid="login-link">
                      {t("nav_login")}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium btn-glow" data-testid="register-link">
                      {t("nav_register")}
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-zinc-400 hover:text-white"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-zinc-800 bg-zinc-900/95 backdrop-blur-xl">
            <nav className="px-4 py-4 space-y-2">
              {/* Language Toggle Mobile */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-zinc-400 hover:bg-zinc-800 rounded-sm"
              >
                <Globe className="h-4 w-4" />
                {language === "de" ? "English" : "Deutsch"}
              </button>
              
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-sm text-sm font-medium ${
                    isActive(link.path)
                      ? "bg-cyan-500/10 text-cyan-400"
                      : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-zinc-800 space-y-2">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:bg-zinc-800 hover:text-white rounded-sm"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      {t("nav_dashboard")}
                    </Link>
                    <Link
                      to="/loyalty"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:bg-zinc-800 hover:text-white rounded-sm"
                    >
                      <Star className="h-4 w-4" />
                      {t("loyalty_title")}
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-amber-400 hover:bg-zinc-800 rounded-sm"
                      >
                        <Shield className="h-4 w-4" />
                        {t("nav_admin")}
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-400 hover:bg-zinc-800 rounded-sm"
                    >
                      <LogOut className="h-4 w-4" />
                      {t("nav_logout")}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-zinc-400 hover:bg-zinc-800 hover:text-white rounded-sm"
                    >
                      {t("nav_login")}
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 rounded-sm"
                    >
                      {t("nav_register")}
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-16">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-zinc-900/50 border-t border-zinc-800 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/icons/icon-72x72.png" alt="Adrian's Logo" className="h-8 w-8 rounded-sm" />
                <span className="text-lg font-bold text-white font-['Chivo']">Adrian's</span>
              </div>
              <p className="text-sm text-zinc-500">
                {language === "de" 
                  ? "Professionelle Fahrzeugaufbereitung für anspruchsvolle Kunden."
                  : "Professional car detailing for discerning customers."}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">{t("nav_services")}</h4>
              <ul className="space-y-2 text-sm text-zinc-500">
                <li><Link to="/services" className="hover:text-cyan-400">{language === "de" ? "Innenreinigung" : "Interior Cleaning"}</Link></li>
                <li><Link to="/services" className="hover:text-cyan-400">{language === "de" ? "Außenreinigung" : "Exterior Cleaning"}</Link></li>
                <li><Link to="/services" className="hover:text-cyan-400">{language === "de" ? "Komplettaufbereitung" : "Complete Detailing"}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">{language === "de" ? "Unternehmen" : "Company"}</h4>
              <ul className="space-y-2 text-sm text-zinc-500">
                <li><Link to="/membership" className="hover:text-cyan-400">{t("nav_membership")}</Link></li>
                <li><Link to="/products" className="hover:text-cyan-400">{t("nav_products")}</Link></li>
                <li><Link to="/loyalty" className="hover:text-cyan-400">{t("loyalty_title")}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">{t("footer_contact")}</h4>
              <ul className="space-y-2 text-sm text-zinc-500">
                <li>stoi@adrians-fahrzeugaufbereitung.de</li>
                <li>+49 160 99858624</li>
                <li>{t("footer_hours")}: Mo-Fr</li>
                <li>08:00 - 13:00 | 14:00 - 17:00</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 mt-8 pt-8 text-center text-sm text-zinc-600">
            © 2025 Adrian's Fahrzeugaufbereitung. {t("footer_rights")}.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
